import CryptoJS from 'crypto-js';

class AdminAuthService {
  constructor() {
    this.sessionKey = 'admin_session';
    this.sessionTimeout = 30 * 60 * 1000; // 30 minutes
    this.maxAttempts = 3;
    this.lockoutTime = 15 * 60 * 1000; // 15 minutes
    this.attemptsKey = 'admin_attempts';
    this.lockoutKey = 'admin_lockout';
  }

  // Check if account is locked out
  isLockedOut() {
    const lockout = localStorage.getItem(this.lockoutKey);
    if (!lockout) return false;
    
    const lockoutTime = parseInt(lockout);
    if (Date.now() > lockoutTime) {
      localStorage.removeItem(this.lockoutKey);
      localStorage.removeItem(this.attemptsKey);
      return false;
    }
    return true;
  }

  // Get remaining lockout time
  getLockoutTimeRemaining() {
    const lockout = localStorage.getItem(this.lockoutKey);
    if (!lockout) return 0;
    
    const remaining = parseInt(lockout) - Date.now();
    return Math.max(0, Math.ceil(remaining / 1000));
  }

  // Record failed attempt
  recordFailedAttempt() {
    const attempts = parseInt(localStorage.getItem(this.attemptsKey) || '0') + 1;
    localStorage.setItem(this.attemptsKey, attempts.toString());
    
    if (attempts >= this.maxAttempts) {
      localStorage.setItem(this.lockoutKey, (Date.now() + this.lockoutTime).toString());
      console.warn('Admin account locked due to multiple failed attempts');
    }
  }

  // Clear failed attempts
  clearFailedAttempts() {
    localStorage.removeItem(this.attemptsKey);
    localStorage.removeItem(this.lockoutKey);
  }

  // Validate credentials with environment variables
  validateCredentials(username, password) {
    if (this.isLockedOut()) {
      throw new Error(`Account locked. Try again in ${this.getLockoutTimeRemaining()} seconds.`);
    }

    // Input validation
    if (!username || !password) {
      throw new Error('Username and password are required');
    }

    if (typeof username !== 'string' || typeof password !== 'string') {
      throw new Error('Invalid input format');
    }

    // Length validation
    if (username.length > 50 || password.length > 100) {
      throw new Error('Input too long');
    }

    // Sanitize inputs
    const cleanUsername = username.trim();
    const cleanPassword = password.trim();

    // Check credentials
    const validUsername = process.env.VUE_APP_ADMIN_USERNAME;
    const validPassword = process.env.VUE_APP_ADMIN_PASSWORD;

    if (!validUsername || !validPassword) {
      throw new Error('Admin credentials not configured');
    }

    if (cleanUsername !== validUsername || cleanPassword !== validPassword) {
      this.recordFailedAttempt();
      throw new Error('Invalid credentials');
    }

    this.clearFailedAttempts();
    return true;
  }

  // Create secure session
  createSession() {
    const sessionData = {
      timestamp: Date.now(),
      expires: Date.now() + this.sessionTimeout,
      token: this.generateSecureToken()
    };

    // Encrypt session data
    const encrypted = CryptoJS.AES.encrypt(
      JSON.stringify(sessionData), 
      process.env.VUE_APP_ADMIN_SESSION_SECRET || 'fallback_secret'
    ).toString();

    localStorage.setItem(this.sessionKey, encrypted);
    return sessionData.token;
  }

  // Validate existing session
  validateSession() {
    try {
      const encrypted = localStorage.getItem(this.sessionKey);
      if (!encrypted) return false;

      // Decrypt session data
      const decrypted = CryptoJS.AES.decrypt(
        encrypted, 
        process.env.VUE_APP_ADMIN_SESSION_SECRET || 'fallback_secret'
      ).toString(CryptoJS.enc.Utf8);

      if (!decrypted) return false;

      const sessionData = JSON.parse(decrypted);
      
      // Check expiration
      if (Date.now() > sessionData.expires) {
        this.logout();
        return false;
      }

      // Extend session if valid
      sessionData.expires = Date.now() + this.sessionTimeout;
      const newEncrypted = CryptoJS.AES.encrypt(
        JSON.stringify(sessionData), 
        process.env.VUE_APP_ADMIN_SESSION_SECRET || 'fallback_secret'
      ).toString();
      
      localStorage.setItem(this.sessionKey, newEncrypted);
      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      this.logout();
      return false;
    }
  }

  // Generate secure token
  generateSecureToken() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Logout
  logout() {
    localStorage.removeItem(this.sessionKey);
  }

  // Check if currently authenticated
  isAuthenticated() {
    return this.validateSession();
  }
}

export default new AdminAuthService();